import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    PwSession,
    UpdatePwSessionRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/pw-and-session/api/pwSession.api.ts';
import {
    getApiPwSession,
    updateApiPwSession,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/pw-and-session/api/pwSession.api.ts';
import { PW_SESSION_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/pw-and-session/constants.ts';
import { wasStepAlreadyPublished } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/pw-and-session/utils.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import { extractError } from '@utils/slice.ts';

export interface PwSessionError {
    message?: string;
    [k: string]: any;
}

export interface PwSessionState {
    error: PwSessionError | null;
    pwSession: PwSession | null;
    isGetPwSessionPending: boolean;
    isUpdatePwSessionPending: boolean;
}

const initialState: PwSessionState = {
    error: null,
    pwSession: null,
    isGetPwSessionPending: false,
    isUpdatePwSessionPending: false,
};

export const getPwSession = createAsyncThunk<
    PwSession,
    { clientId: number; tenantAlias: string },
    { rejectValue: PwSessionError }
>('adminCenterPwSession/getApiPwSession', ({ clientId, tenantAlias }, { rejectWithValue, dispatch }) =>
    getApiPwSession(clientId, tenantAlias)
        .then((res) => {
            if (wasStepAlreadyPublished(res)) {
                dispatch(setPublishedSteps(PW_SESSION_STEP_TITLE));
            }

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const updatePwSession = createAsyncThunk<
    string,
    { clientId: number; tenantAlias: string; payload: UpdatePwSessionRequest },
    { rejectValue: PwSessionError }
>('adminCenterPwSession/updateApiPwSession', ({ clientId, tenantAlias, payload }, { rejectWithValue, dispatch }) =>
    updateApiPwSession(clientId, tenantAlias, payload)
        .then((res) => {
            dispatch(setPublishedSteps(PW_SESSION_STEP_TITLE));

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

const pwSessionSlice = createSlice({
    name: 'adminCenterPwSession',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getPwSession.pending, (state) => {
            state.isGetPwSessionPending = true;
            state.error = null;
        });
        builder.addCase(getPwSession.fulfilled, (state, action) => {
            state.isGetPwSessionPending = false;
            state.pwSession = action.payload;
        });
        builder.addCase(getPwSession.rejected, (state, action) => {
            state.isGetPwSessionPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updatePwSession.pending, (state) => {
            state.isUpdatePwSessionPending = true;
            state.error = null;
        });
        builder.addCase(updatePwSession.fulfilled, (state) => {
            state.isUpdatePwSessionPending = false;
        });
        builder.addCase(updatePwSession.rejected, (state, action) => {
            state.isUpdatePwSessionPending = false;
            state.error = extractError(action);
        });
    },
});

export default pwSessionSlice.reducer;
